<template>
  <div class="raffle-config-form">
    <div v-if="loading" class="text-center">
      <v-progress-circular :size="70" :width="7" color="#6E17A5" indeterminate></v-progress-circular>
    </div>
    <div v-else>
      <!-- Global Config Badge -->
      <div v-if="isUsingGlobalConfig" class="mb-6">
        <v-chip color="#F3E5F5" text-color="#6E17A5" class="font-weight-bold">
          {{ $t('company-config.globalConfig.label') }}
        </v-chip>
      </div>

      <!-- Resumen Configuración -->
      <v-card class="elevation-2 rounded-lg mb-6">
        <v-card-text class="pa-6">
          <div class="d-flex align-center mb-6">
            <v-avatar color="#f3e8ff" size="54" class="mr-4">
              <v-icon color="#6E17A5" size="30">mdi-clipboard-list</v-icon>
            </v-avatar>
            <div>
              <h2 class="text-h5 font-weight-bold mb-1" style="color: #6E17A5;">
                {{ $t('company-config.raffle.summary.title') }}
              </h2>
              <p class="text--secondary mt-0">
                {{ $t('company-config.raffle.summary.description') }}
              </p>
            </div>
          </div>
          <v-row>
            <v-col cols="12" sm="2" class="d-flex align-center px-4">
              <v-avatar color="#f3e8ff" size="40" class="mr-3 flex-shrink-0">
                <v-icon color="#6E17A5" size="22">mdi-star-circle</v-icon>
              </v-avatar>
              <div style="min-width: 120px;">
                <p class="text--secondary text-caption mb-0">{{ $t('company-config.summary.totalStars') }}</p>
                <h3 class="text-h4 font-weight-bold" style="color: #6E17A5">{{ botTotalStars }}</h3>
              </div>
            </v-col>
            <v-col cols="12" sm="2" class="d-flex align-center px-4">
              <v-avatar color="#f3e8ff" size="40" class="mr-3 flex-shrink-0">
                <v-icon color="#6E17A5" size="22">mdi-target</v-icon>
              </v-avatar>
              <div style="min-width: 120px;">
                <p class="text--secondary text-caption mb-0">{{ $t('company-config.raffle.probabilidadGanar.label') }}</p>
                <h3 class="text-h4 font-weight-bold" style="color: #6E17A5">{{ ((1 - summaryData.possibleWinnerRaffle) * 100).toFixed(0) }}%</h3>
              </div>
            </v-col>
            <v-col cols="12" sm="2" class="d-flex align-center px-4">
              <v-avatar color="#f3e8ff" size="40" class="mr-3 flex-shrink-0">
                <v-icon color="#6E17A5" size="22">mdi-trophy-outline</v-icon>
              </v-avatar>
              <div style="min-width: 120px;">
                <p class="text--secondary text-caption mb-0">{{ $t('company-config.raffle.raffleMaxTimeWinner.label') }}</p>
                <h3 class="text-h4 font-weight-bold" style="color: #6E17A5">{{ summaryData.raffleMaxTimeWinner || 0 }}</h3>
              </div>
            </v-col>
            <v-col cols="12" sm="2" class="d-flex align-center px-4">
              <v-avatar color="#f3e8ff" size="40" class="mr-3 flex-shrink-0">
                <v-icon color="#6E17A5" size="22">mdi-calendar-clock</v-icon>
              </v-avatar>
              <div style="min-width: 120px;">
                <p class="text--secondary text-caption mb-0">{{ $t('company-config.raffle.raffleNextTimeWinnerAgain.label') }}</p>
                <h3 class="text-h4 font-weight-bold" style="color: #6E17A5">{{ summaryData.raffleNextTimeWinnerAgain || 0 }} {{ $t('company-config.summary.days') }}</h3>
              </div>
            </v-col>
            <v-col cols="12" sm="2" class="d-flex align-center px-4">
              <v-avatar color="#f3e8ff" size="40" class="mr-3 flex-shrink-0">
                <v-icon color="#6E17A5" size="22">mdi-star-outline</v-icon>
              </v-avatar>
              <div style="min-width: 120px;">
                <p class="text--secondary text-caption mb-0">{{ $t('company-config.raffle.categories.label') }}</p>
                <h3 class="text-h4 font-weight-bold" style="color: #6E17A5">{{ summaryCategoriesCount }}</h3>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Grid Layout -->
      <v-row>
        <!-- Reglas Generales -->
        <v-col cols="12" md="6">
          <v-card class="elevation-2 rounded-lg">
            <v-card-text class="pa-6">
              <div class="d-flex align-center mb-6">
                <v-avatar color="#f3e8ff" size="54" class="mr-4">
                  <v-icon color="#6E17A5" size="30">mdi-target</v-icon>
                </v-avatar>
                <div>
                  <h2 class="text-h5 font-weight-bold" style="color: #6E17A5;">
                    {{ $t('company-config.rules.title') }}
                  </h2>
                  <p class="text--secondary mt-1">
                    {{ $t('company-config.rules.description') }}
                  </p>
                </div>
              </div>

              <v-form ref="raffleForm" lazy-validation>
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="formData.probabilidadGanar"
                      :label="$t('company-config.raffle.probabilidadGanar.label')"
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      :rules="probabilidadGanarRules"
                      required
                      outlined
                      rounded
                      dense
                      suffix="%"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      :value="(probabilidadPerder * 100).toFixed(1)"
                      :label="$t('company-config.raffle.probabilidadPerder.label')"
                      type="number"
                      min="0"
                      max="100"
                      outlined
                      rounded
                      dense
                      suffix="%"
                      readonly
                      disabled
                      background-color="#f5f5f5"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="formData.raffleMaxTimeWinner"
                      :label="$t('company-config.raffle.raffleMaxTimeWinner.label')"
                      type="number"
                      min="1"
                      :rules="raffleMaxTimeWinnerRules"
                      required
                      outlined
                      rounded
                      dense
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="formData.raffleNextTimeWinnerAgain"
                      :label="$t('company-config.raffle.raffleNextTimeWinnerAgain.label')"
                      type="number"
                      min="0"
                      :rules="raffleNextTimeWinnerAgainRules"
                      required
                      outlined
                      rounded
                      dense
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="formData.raffleMonthStartDay"
                      :label="$t('company-config.raffle.raffleMonthStartDay.label')"
                      type="number"
                      min="1"
                      max="28"
                      :rules="raffleMonthStartDayRules"
                      required
                      outlined
                      rounded
                      dense
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Categorías Participantes -->
        <v-col cols="12" md="6">
          <v-card class="elevation-2 rounded-lg">
            <v-card-text class="pa-6">
              <div class="d-flex justify-space-between align-center mb-6">
                <div class="d-flex align-center">
                  <v-avatar color="#f3e8ff" size="54" class="mr-4">
                    <v-icon color="#6E17A5" size="30">mdi-star-outline</v-icon>
                  </v-avatar>
                  <div>
                    <h2 class="text-h5 font-weight-bold" style="color: #6E17A5;">
                      {{ $t('company-config.raffle.categories.label') }}
                    </h2>
                    <p class="text--secondary mt-1">
                      Configura {{ $t('company-config.raffle.categories.label') }} y {{ $t('company-config.raffle.whiteStars.label') }}.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Categorías Normales -->
              <div v-if="allCategoriesList.length > 0" class="mb-4">
                <div class="mb-3">
                  <v-chip color="#D4EDDA" text-color="#155724" class="font-weight-bold" small>
                    {{ $t('company-config.raffle.categories.label') }}
                  </v-chip>
                </div>

                <!-- Categorías seleccionadas -->
                <div v-if="selectedCategoriesList.length > 0" class="mb-4">
                  <div class="text-body-2 font-weight-medium mb-2" style="color: #6E17A5;">
                    {{ $t('company-config.categories.selected') }}
                  </div>
                  <div v-for="category in selectedCategoriesList" :key="category.recognitionCategoryId" class="mb-2">
                    <div class="d-flex align-center">
                        <v-img
                          :src="category.icon"
                          max-width="24"
                          max-height="24"
                          class="mr-2"
                          contain
                        ></v-img>
                      <v-checkbox
                        v-model="selectedCategories"
                        :value="category.recognitionCategoryId"
                        :label="category.categoryName"
                        color="#6E17A5"
                        hide-details
                        class="mt-0"
                      ></v-checkbox>
                      <v-tooltip right>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            :color="getCategoryStarCount(category.recognitionCategoryId) === 0 ? 'error' : 'success'"
                            small
                            class="ml-2"
                          >
                            {{ getCategoryStarCount(category.recognitionCategoryId) === 0 ? 'mdi-alert-circle' : 'mdi-check-circle' }}
                          </v-icon>
                        </template>
                        <span>{{ getCategoryStarCount(category.recognitionCategoryId) === 0 ? $t('company-config.raffle.stars.unavailable') : `${getCategoryStarCount(category.recognitionCategoryId)} ${$t('company-config.raffle.stars.available')}` }}</span>
                      </v-tooltip>
                    </div>
                    <!-- Valores de White Star (se muestran debajo de la categoría White en seleccionadas) -->
                    <div v-if="category.recognitionCategoryId === 999 && availableWhiteStarValues.length > 0" class="ml-8 mt-2">
                      <div v-for="value in availableWhiteStarValues" :key="value.value" class="mb-2">
                        <div class="d-flex align-center">
                          <v-checkbox
                            v-model="selectedWhiteStars"
                            :value="String(value.value)"
                            :label="`$${formatNumber(value.value)}`"
                            color="#6E17A5"
                            hide-details
                            class="mt-0"
                            dense
                          ></v-checkbox>
                          <v-tooltip right>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon
                              v-bind="attrs"
                              v-on="on"
                              :color="value.count === 0 ? 'error' : 'success'"
                              small
                              class="ml-2"
                              >
                                {{ value.count === 0 ? 'mdi-alert-circle' : 'mdi-check-circle' }}
                              </v-icon>
                            </template>
                            <span>{{ value.count === 0 ? $t('company-config.raffle.stars.unavailable') : `${value.count} ${$t('company-config.raffle.stars.available')}` }}</span>
                          </v-tooltip>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Categorías disponibles -->
                <div v-if="availableCategoriesList.length > 0" class="mb-4">
                  <div class="text-body-2 font-weight-medium mb-2" style="color: #666;">
                    {{ $t('company-config.categories.available') }}
                  </div>
                  <div v-for="category in availableCategoriesList" :key="category.recognitionCategoryId" class="mb-2">
                    <div class="d-flex align-center">
                      <v-img
                        :src="category.icon"
                        max-width="24"
                        max-height="24"
                        class="mr-2"
                        contain
                      ></v-img>
                      <v-checkbox
                        v-model="selectedCategories"
                        :value="category.recognitionCategoryId"
                        :label="category.categoryName"
                        color="#6E17A5"
                        hide-details
                        class="mt-0"
                      ></v-checkbox>
                      <v-tooltip right>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            :color="getCategoryStarCount(category.recognitionCategoryId) === 0 ? 'error' : 'success'"
                            small
                            class="ml-2"
                          >
                            {{ getCategoryStarCount(category.recognitionCategoryId) === 0 ? 'mdi-alert-circle' : 'mdi-check-circle' }}
                          </v-icon>
                        </template>
                        <span>{{ getCategoryStarCount(category.recognitionCategoryId) === 0 ? $t('company-config.raffle.stars.unavailable') : `${getCategoryStarCount(category.recognitionCategoryId)} ${$t('company-config.raffle.stars.available')}` }}</span>
                      </v-tooltip>
                    </div>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Botón de Acción -->
      <v-tooltip left>
        <template v-slot:activator="{ attrs }">
          <v-btn
            fab
            fixed
            bottom
            right
            dark
            large
            color="primary"
            v-bind="attrs"
            :loading="loading"
            :disabled="loading"
            @click="submitForm"
          >
            <v-icon dark>mdi-content-save</v-icon>
          </v-btn>
        </template>
        <span>{{ $t('button.save') }}</span>
      </v-tooltip>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import Swal from 'sweetalert2';

export default {
  name: 'RaffleConfigForm',
  props: {
    companyId: {
      type: [String, Number],
      required: true
    },
    initialData: {
      type: Object,
      default: () => ({})
    },
    isUsingGlobalConfig: {
      type: Boolean,
      default: false
    },
    allCategories: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      formData: {
        possibleWinnerRaffle: null,
        probabilidadGanar: null,
        raffleMaxTimeWinner: null,
        raffleNextTimeWinnerAgain: null,
        raffleMonthStartDay: null,
        categoriesRaffle: []
      },
      summaryCategoriesCount: 0,
      selectedCategories: [],
      selectedWhiteStars: [],
      loading: false,
      summaryData: {
        possibleWinnerRaffle: 0,
        raffleMaxTimeWinner: 0,
        raffleNextTimeWinnerAgain: 0
      },
      probabilidadGanarRules: [
        v => v !== null && v !== undefined && v !== '' || this.$t('company-config.raffle.probabilidadGanar.required'),
        v => (v >= 0 && v <= 100) || this.$t('company-config.raffle.probabilidadGanar.range')
      ],
      raffleMaxTimeWinnerRules: [
        v => v !== null && v !== undefined && v !== '' || this.$t('company-config.raffle.raffleMaxTimeWinner.required'),
        v => v >= 1 || this.$t('company-config.raffle.raffleMaxTimeWinner.minValue')
      ],
      raffleNextTimeWinnerAgainRules: [
        v => v !== null && v !== undefined && v !== '' || this.$t('company-config.raffle.raffleNextTimeWinnerAgain.required'),
        v => v >= 0 || this.$t('company-config.raffle.raffleNextTimeWinnerAgain.minValue')
      ],
      raffleMonthStartDayRules: [
        v => !!v || this.$t('company-config.raffle.raffleMonthStartDay.required'),
        v => v >= 1 && v <= 28 || this.$t('company-config.raffle.raffleMonthStartDay.range')
      ]
    };
  },
  computed: {
    allCategoriesList() {
      if (!this.allCategories || !Array.isArray(this.allCategories)) return [];
      
      const normalCategories = this.allCategories
        .filter(cat => cat.recognitionCategoryId !== 999)
        .sort((a, b) => a.recognitionCategoryId - b.recognitionCategoryId);

      const whiteCategories = this.allCategories.filter(cat => cat.recognitionCategoryId === 999);
      let whiteCategory = null;
      if (whiteCategories.length > 0) {
        whiteCategory = {
          recognitionCategoryId: 999,
          categoryName: 'White',
          icon: whiteCategories[0].icon,
          unitPrice: whiteCategories[0].unitPrice
        };
      }

      return whiteCategory ? [...normalCategories, whiteCategory] : normalCategories;
    },
    selectedCategoriesList() {
      return this.allCategoriesList
        .filter(cat => this.selectedCategories.includes(cat.recognitionCategoryId))
        .sort((a, b) => a.recognitionCategoryId - b.recognitionCategoryId);
    },
    availableCategoriesList() {
      return this.allCategoriesList
        .filter(cat => !this.selectedCategories.includes(cat.recognitionCategoryId))
        .sort((a, b) => a.recognitionCategoryId - b.recognitionCategoryId);
    },
    botStarStock() {
      return this.initialData?.botStarStock || [];
    },
    botTotalStars() {
      return this.initialData?.botTotalStars || 0;
    },
    botHealthy() {
      return this.initialData?.botHealthy !== undefined ? this.initialData.botHealthy : true;
    },
    availableWhiteStarValues() {
      if (!this.allCategories || !Array.isArray(this.allCategories)) return [];
      
      // Obtener valores del parámetro (categoriesRaffle con categoryId 999)
      const paramCategories = this.formData.categoriesRaffle || this.initialData?.raffle?.categoriesRaffle || [];
      const paramWhiteValues = paramCategories.filter(cat => cat.categoryId === 999 && cat.value);
      
      // Obtener valores del botStarStock (todos los valores de White Star disponibles en el bot)
      const botWhiteValues = this.botStarStock
        .filter(item => item.categoryId === 999 && item.value)
        .map(item => item.value);
      
      // Fusionar ambos listados usando Set para evitar duplicados
      const allValuesSet = new Set();
      
      // Agregar valores del parámetro (configuración guardada)
      paramWhiteValues.forEach(cat => {
        allValuesSet.add(cat.value);
      });
      
      // Agregar valores del bot
      botWhiteValues.forEach(value => {
        allValuesSet.add(value);
      });
      
      // Convertir a array y mapear con count SIEMPRE del bot
      const values = Array.from(allValuesSet).map(value => ({
        value: value,
        count: this.getCategoryStarCount(999, value), // SIEMPRE usar count del bot
        categoryName: `White - $${value}`,
        source: 'merged'
      })).sort((a, b) => a.value - b.value);
      
      return values;
    },
    probabilidadPerder() {
      if (this.formData.probabilidadGanar === null || this.formData.probabilidadGanar === undefined) return 0;
      const ganar = parseFloat(this.formData.probabilidadGanar) / 100;
      return Math.max(0, Math.min(1, 1 - ganar));
    }
  },
  watch: {
    initialData: {
      handler(newVal) {
        if (newVal) {
          const raffle = this.getRaffleData(newVal);
          
          this.copyValuesToSummary(raffle);
          
          this.formData = this.createFormData(raffle);
          
          this.parseCategoriesRaffle(raffle.categoriesRaffle);
        }
      },
      immediate: true,
      deep: true
    },
    selectedCategories: {
      handler(newVal) {
        // Si se deselecciona la categoría White (999), limpiar los valores de White seleccionados
        if (!newVal.includes(999)) {
          this.selectedWhiteStars = [];
        }
      }
    }
  },
  methods: {
    ...mapActions('COMPANYCONFIG', ['ACTION_PUT_COMPANY_RAFFLE_CONFIG']),
    getRaffleData(newVal) {
      return newVal.raffle || newVal;
    },
    copyValuesToSummary(raffle) {
      this.summaryData = {
        possibleWinnerRaffle: raffle.possibleWinnerRaffle || 0,
        raffleMaxTimeWinner: raffle.raffleMaxTimeWinner || 0,
        raffleNextTimeWinnerAgain: raffle.raffleNextTimeWinnerAgain || 0
      };

      this.summaryCategoriesCount = raffle.categoriesRaffle?.length || 0;
    },
    calculateWinningProbability(raffle) {
      return raffle.possibleWinnerRaffle !== undefined
        ? (1 - raffle.possibleWinnerRaffle) * 100
        : null;
    },
    createFormData(raffle) {
      const probabilidadGanar = this.calculateWinningProbability(raffle);
      
      return {
        possibleWinnerRaffle: raffle.possibleWinnerRaffle !== undefined ? raffle.possibleWinnerRaffle : null,
        probabilidadGanar: probabilidadGanar,
        raffleMaxTimeWinner: raffle.raffleMaxTimeWinner !== undefined ? raffle.raffleMaxTimeWinner : null,
        raffleNextTimeWinnerAgain: raffle.raffleNextTimeWinnerAgain !== undefined ? raffle.raffleNextTimeWinnerAgain : null,
        raffleMonthStartDay: raffle.raffleMonthStartDay !== undefined ? raffle.raffleMonthStartDay : null,
        categoriesRaffle: raffle.categoriesRaffle || []
      };
    },
    resetForm() {
      this.formData = {
        possibleWinnerRaffle: null,
        probabilidadGanar: null,
        raffleMaxTimeWinner: null,
        raffleNextTimeWinnerAgain: null,
        raffleMonthStartDay: null,
        categoriesRaffle: []
      };
      this.selectedCategories = [];
      this.selectedWhiteStars = [];
      if (this.$refs.raffleForm) {
        this.$refs.raffleForm.resetValidation();
      }
    },
    async submitForm() {
      if (!this.$refs.raffleForm.validate()) {
        return;
      }

      if (this.selectedCategories.length === 0 && this.selectedWhiteStars.length === 0) {
        Swal.fire({
          icon: 'error',
          title: this.$t('company-config.raffle.categories.required'),
          confirmButtonColor: '#6E17A5'
        });
        return;
      }

      if (this.selectedCategories.includes(999) && this.selectedWhiteStars.length === 0) {
        Swal.fire({
          icon: 'error',
          title: this.$t('company-config.raffle.whiteStars.required'),
          confirmButtonColor: '#6E17A5'
        });
        return;
      }

      const result = await Swal.fire({
        title: this.$t('company-config.raffle.actionSave.confirm'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('button.save.text'),
        cancelButtonText: this.$t('button.cancel.text'),
        confirmButtonColor: '#6E17A5'
      });

      if (!result.isConfirmed) {
        return;
      }

      this.loading = true;
      try {
        const categoriesRaffle = [
          ...this.selectedCategories
            .filter(catId => catId !== 999)
            .map(catId => ({ categoryId: catId })),
          ...this.selectedWhiteStars.map(value => ({ categoryId: 999, value: value }))
        ];

        const dataToSubmit = {
          possibleWinnerRaffle: this.probabilidadPerder,
          raffleMaxTimeWinner: parseInt(this.formData.raffleMaxTimeWinner),
          raffleNextTimeWinnerAgain: parseInt(this.formData.raffleNextTimeWinnerAgain),
          raffleMonthStartDay: parseInt(this.formData.raffleMonthStartDay),
          categoriesRaffle: categoriesRaffle
        };

        await this.ACTION_PUT_COMPANY_RAFFLE_CONFIG({ companyId: this.companyId, data: dataToSubmit });

        // Emitir evento para que el padre recargue la configuración
        this.$emit('config-saved');

        Swal.fire({
          icon: 'success',
          title: this.$t('company-config.raffle.actionSave.success'),
          confirmButtonColor: '#6E17A5'
        });
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: this.$t('label.invalid.title'),
          text: this.$t('label.invalid.text'),
          confirmButtonColor: '#6E17A5'
        });
      } finally {
        this.loading = false;
      }
    },
    parseCategoriesRaffle(categories) {
      if (!categories || !Array.isArray(categories)) return;

      this.selectedCategories = [];
      this.selectedWhiteStars = [];

      
      categories.forEach(cat => {
        if (cat.categoryId === 999 && cat.value) {
          this.selectedWhiteStars.push(String(cat.value));
          this.selectedCategories.push(999);
        } else {
          this.selectedCategories.push(cat.categoryId);
        }
      });
      
    },
    formatNumber(value) {
      return new Intl.NumberFormat('es-CO').format(value);
    },
    getCategoryStarCount(categoryId, value = null) {

      if (value === null) {
        return this.botStarStock
          .filter(item => item.categoryId === categoryId)
          .reduce((total, item) => total + (item.count || 0), 0);
      }

      const stockItem = this.botStarStock.find(
        item =>
          item.categoryId === categoryId &&
          item.value === value
      );

      return stockItem ? stockItem.count : 0;
    }
  },
  async mounted() {
  }
};
</script>

<style scoped>
.info-badge {
  text-align: center;
}
</style>
